import { ArrowLeft, ArrowRight, CalendarDays, Check, Clock3, MapPin, Users } from "lucide-react"
import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../../components/Animate/Animate"
import InputField from "../../components/InputField/InputField"
import { usePublicViewContext } from "../../context/usePublicViewContext"
import { useConfirmation } from "../../context/useConfirmationContext"
import { EAppView } from "../../Enum/EAppViewType"
import { useRestaurantReservations } from "../../hooks/RestaurantReservation/useRestaurantReservations"
import { companyMemory } from "../../Objects/Memory/Memory"
import type {
  PublicRestaurantReservationConfiguration,
  RestaurantReservation,
  RestaurantReservationSlot,
} from "../../Objects/RestaurantReservation/RestaurantReservationTypes"
import {
  clearPendingRestaurantReservation,
  getPendingRestaurantReservation,
  savePendingRestaurantReservation,
} from "../../utils/restaurantReservationDraft"
import { getPublicMenuTheme } from "../Helpers/PublicMenuTheme"

interface RestaurantReservationsViewProps {
  restaurantSlug: string
  configuration: PublicRestaurantReservationConfiguration | null | undefined
}

type ReservationStep = "area" | "details" | "time" | "confirm" | "success"

const RESERVATION_STEPS: Exclude<ReservationStep, "success">[] = ["area", "details", "time", "confirm"]

const toDateInput = (date: Date) => {
  const year = date.getFullYear()

  const month = String(date.getMonth() + 1).padStart(2, "0")

  const day = String(date.getDate()).padStart(2, "0")

  return `${year}-${month}-${day}`
}

export const RestaurantReservationsView: React.FC<RestaurantReservationsViewProps> = ({
  restaurantSlug,
  configuration,
}) => {
  const { t } = useTranslation("restaurantReservations")

  const { auth, setCurrentView } = usePublicViewContext()

  const { setNewConfirmation } = useConfirmation()

  const reservationsApi = useRestaurantReservations()

  const theme = getPublicMenuTheme(companyMemory)

  const [mine, setMine] = useState<RestaurantReservation[]>([])

  const [slots, setSlots] = useState<RestaurantReservationSlot[]>([])

  const [step, setStep] = useState<ReservationStep>("area")

  const [areaId, setAreaId] = useState(0)

  const [date, setDate] = useState(toDateInput(new Date()))

  const [partySize, setPartySize] = useState("2")

  const [time, setTime] = useState("")

  const [notes, setNotes] = useState("")

  const [renderedAt] = useState(() => Date.now())

  const selectedArea = useMemo(
    () => configuration?.areas.find((area) => area.id === areaId),
    [areaId, configuration?.areas],
  )

  const minimumDate = toDateInput(new Date())

  const maximumDate = useMemo(() => {
    const maximum = new Date()
    maximum.setDate(maximum.getDate() + (configuration?.maximumAdvanceDays ?? 30))
    return toDateInput(maximum)
  }, [configuration?.maximumAdvanceDays])

  const maximumPartySize = Math.min(
    configuration?.maximumPartySize ?? 1,
    selectedArea?.capacity ?? Number.MAX_SAFE_INTEGER,
  )

  const numericPartySize = Number(partySize)

  const hasValidDetails = Boolean(
    date >= minimumDate &&
    date <= maximumDate &&
    Number.isInteger(numericPartySize) &&
    numericPartySize >= (configuration?.minimumPartySize ?? 1) &&
    numericPartySize <= maximumPartySize,
  )

  const activeStepIndex = step === "success" ? RESERVATION_STEPS.length : RESERVATION_STEPS.indexOf(step)

  const progress = step === "success" ? 100 : ((activeStepIndex + 1) / RESERVATION_STEPS.length) * 100

  const loadMine = async () => {
    if (!auth.customer) return
    const data = await reservationsApi.getMine(restaurantSlug)
    if (data) setMine(data)
  }

  useEffect(() => {
    if (configuration) {
      const pendingReservation = getPendingRestaurantReservation(restaurantSlug)

      const pendingAreaExists = configuration.areas.some((area) => area.id === pendingReservation?.areaId)

      setAreaId(pendingAreaExists ? pendingReservation!.areaId : 0)

      setDate(pendingReservation?.date ?? toDateInput(new Date()))

      setPartySize(String(pendingReservation?.partySize ?? Math.max(configuration.minimumPartySize, 2)))

      setTime(pendingAreaExists ? (pendingReservation?.time ?? "") : "")

      setNotes(pendingReservation?.notes ?? "")

      setStep(pendingAreaExists && pendingReservation?.time ? "confirm" : "area")
    }
    if (auth.customer) {
      void reservationsApi.getMine(restaurantSlug).then((data) => {
        if (data) setMine(data)
      })
    }
  }, [auth.customer?.id, configuration, restaurantSlug])

  const selectArea = (id: number) => {
    setAreaId(id)

    setSlots([])

    setTime("")

    setStep("details")
  }

  const findSlots = async () => {
    if (!hasValidDetails) return

    setTime("")

    const data = await reservationsApi.getAvailability(restaurantSlug, areaId, date, numericPartySize)

    if (!data) return

    setSlots(data)

    setStep("time")
  }

  const createReservation = async () => {
    if (!time) return

    const request = {
      areaId,
      date,
      time,
      partySize: numericPartySize,
      notes: notes.trim() || undefined,
    }

    if (!auth.customer) {
      savePendingRestaurantReservation(restaurantSlug, request)

      setCurrentView(EAppView.LoginView)

      return
    }

    const created = await reservationsApi.create(restaurantSlug, request)

    if (!created) return

    clearPendingRestaurantReservation(restaurantSlug)

    setStep("success")

    await loadMine()
  }

  const startAnotherReservation = () => {
    setAreaId(0)

    setDate(toDateInput(new Date()))

    setPartySize(String(Math.max(configuration?.minimumPartySize ?? 1, 2)))

    setSlots([])

    setTime("")

    setNotes("")

    setStep("area")
  }

  const cardStyle = {
    backgroundColor: theme.surfaceColor,
    borderColor: theme.borderColor,
    borderWidth: theme.borderThickness,
    color: theme.textColor,
  }

  const primaryButtonStyle = { backgroundColor: theme.buttonColor, color: theme.buttonTextColor }

  const mutedSurfaceStyle = { backgroundColor: theme.secondaryColor, color: theme.textColor }

  if (configuration === undefined) {
    return (
      <Animate type={AnimationTypes.fadeInTraslate} duration="1.2s">
        <div
          className="mx-auto max-w-3xl py-20 text-center font-bold"
          style={{ color: theme.emptyStateDescriptionColor }}
        >
          {t("common.loading")}
        </div>
      </Animate>
    )
  }

  if (!configuration?.isEnabled || configuration.areas.length === 0) {
    return (
      <Animate type={AnimationTypes.fadeInTraslate} duration="1.3s">
        <div className="mx-auto max-w-3xl py-20 text-center">
          <h2 className="text-2xl font-black" style={{ color: theme.emptyStateTitleColor }}>
            {t("public.unavailable_title")}
          </h2>

          <p className="mt-3" style={{ color: theme.emptyStateDescriptionColor }}>
            {t("public.unavailable_description")}
          </p>
        </div>
      </Animate>
    )
  }

  if (auth.status && !auth.customer) {
    return (
      <Animate type={AnimationTypes.fadeInTraslate} duration="1.2s">
        <div
          className="mx-auto max-w-3xl py-20 text-center font-bold"
          style={{ color: theme.emptyStateDescriptionColor }}
        >
          {t("common.loading")}
        </div>
      </Animate>
    )
  }

  return (
    <Animate type={AnimationTypes.fadeIn} duration="1.2s">
      <div className="mx-auto max-w-4xl space-y-6 pb-24 pt-4">
        <section className="overflow-hidden rounded-lg shadow-sm" style={cardStyle}>
          {step !== "success" && (
            <Animate type={AnimationTypes.slideDownIn} duration="1.2s">
              <div
                className="border-b px-5 py-4 sm:px-7"
                style={{ borderColor: theme.borderColor, backgroundColor: theme.categoryBackgroundColor }}
              >
                <div className="flex items-center justify-between gap-4 text-xs font-black">
                  <span style={{ color: theme.primaryColor }}>
                    {t("public.step_counter", { current: activeStepIndex + 1, total: RESERVATION_STEPS.length })}
                  </span>

                  <span className="truncate opacity-70">{t(`public.steps.${step}`)}</span>
                </div>

                <div
                  className="mt-3 h-2 overflow-hidden rounded-full"
                  style={{ backgroundColor: theme.secondaryColor }}
                >
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{ width: `${progress}%`, backgroundColor: theme.primaryColor }}
                  />
                </div>

                <div className="mt-3 hidden grid-cols-4 gap-2 sm:grid">
                  {RESERVATION_STEPS.map((item, index) => (
                    <span key={item} className="flex items-center gap-1.5 text-[11px] font-bold opacity-70">
                      <span
                        className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px]"
                        style={
                          index <= activeStepIndex
                            ? {
                                backgroundColor: theme.serviceSelectorActiveBackgroundColor,
                                color: theme.serviceSelectorActiveTextColor,
                              }
                            : mutedSurfaceStyle
                        }
                      >
                        {index < activeStepIndex ? <Check size={12} strokeWidth={3} /> : index + 1}
                      </span>
                      {t(`public.steps.${item}`)}
                    </span>
                  ))}
                </div>
              </div>
            </Animate>
          )}

          <div className="p-5 sm:p-7">
            {step === "area" && (
              <Animate type={AnimationTypes.slideRightIn} duration="1.2s">
                <div>
                  <div className="text-center">
                    <MapPin className="mx-auto" size={30} style={{ color: theme.primaryColor }} />

                    <h2 className="mt-3 text-xl font-black sm:text-2xl">{t("public.choose_title")}</h2>

                    <p className="mt-1 text-sm opacity-65">{t("public.choose_description")}</p>
                  </div>

                  <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {configuration.areas.map((area, index) => (
                      <Animate
                        key={area.id}
                        type={AnimationTypes.fadeInTraslate}
                        duration="1.3s"
                        delay={`${Math.min(index, 6) * 0.08}s`}
                      >
                        <button
                          type="button"
                          data-interaction-description-key="interactionDescriptions:restaurantReservations.public.area"
                          onClick={() => selectArea(area.id)}
                          className="group overflow-hidden rounded-2xl border-2 text-left shadow-sm transition duration-300 hover:-translate-y-0.5 hover:shadow-md active:scale-[0.98]"
                          style={{
                            backgroundColor: theme.surfaceColor,
                            borderColor: theme.borderColor,
                            color: theme.textColor,
                          }}
                        >
                          {area.iconUrl && (
                            <img
                              src={area.iconUrl.toClientRequest()}
                              alt={area.name}
                              className="h-36 w-full object-cover transition duration-500 group-hover:scale-[1.03]"
                            />
                          )}

                          <span className="block p-4">
                            <strong className="block text-base font-black">{area.name}</strong>

                            <span className="mt-1 flex items-center gap-1.5 text-sm font-semibold opacity-65">
                              <Users size={15} /> {t("management.capacity_value", { count: area.capacity })}
                            </span>
                          </span>
                        </button>
                      </Animate>
                    ))}
                  </div>
                </div>
              </Animate>
            )}

            {step === "details" && (
              <Animate type={AnimationTypes.slideRightIn} duration="1.3s">
                <div>
                  <div className="text-center">
                    <CalendarDays className="mx-auto" size={30} style={{ color: theme.primaryColor }} />

                    <h2 className="mt-3 text-xl font-black sm:text-2xl">{t("public.details_title")}</h2>

                    <p className="mt-1 text-sm opacity-65">
                      {t("public.details_description", { area: selectedArea?.name })}
                    </p>
                  </div>

                  <div className="mx-auto mt-6 grid max-w-2xl gap-4 md:grid-cols-2">
                    <InputField
                      type="date"
                      value={date}
                      placeholder={t("fields.date")}
                      min={minimumDate}
                      descriptionKey="interactionDescriptions:restaurantReservations.public.date"
                      max={maximumDate}
                      onChange={(event) => {
                        if (!event.target.value) return
                        setDate(event.target.value)
                        setSlots([])
                        setTime("")
                      }}
                    />

                    <InputField
                      type="number"
                      value={partySize}
                      placeholder={t("fields.party_size")}
                      descriptionKey="interactionDescriptions:restaurantReservations.public.partySize"
                      min={configuration.minimumPartySize}
                      max={maximumPartySize}
                      onChange={(event) => {
                        setPartySize(event.target.value)
                        setSlots([])
                        setTime("")
                      }}
                    />
                  </div>

                  <p className="mx-auto mt-3 max-w-2xl text-center text-xs font-semibold opacity-60">
                    {t("public.party_limit", { count: maximumPartySize, area: selectedArea?.name })}
                  </p>

                  <div className="mt-7 flex gap-3">
                    <button
                      type="button"
                      onClick={() => setStep("area")}
                      data-interaction-description-key="interactionDescriptions:restaurantReservations.public.backToArea"
                      className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl border px-4 font-black transition active:scale-[0.98]"
                      style={{ borderColor: theme.borderColor, color: theme.textColor }}
                    >
                      <ArrowLeft size={18} /> <span className="hidden sm:inline">{t("actions.back")}</span>
                    </button>

                    <button
                      type="button"
                      disabled={reservationsApi.isLoading || !hasValidDetails}
                      onClick={findSlots}
                      data-interaction-description-key="interactionDescriptions:restaurantReservations.public.find"
                      className="inline-flex min-h-12 flex-1 items-center justify-center gap-2 rounded-2xl px-5 text-sm font-black shadow-lg transition hover:-translate-y-0.5 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
                      style={primaryButtonStyle}
                    >
                      {reservationsApi.isLoading ? t("common.loading") : t("public.find_times")}{" "}
                      <ArrowRight size={18} />
                    </button>
                  </div>
                </div>
              </Animate>
            )}

            {step === "time" && (
              <Animate type={AnimationTypes.slideRightIn} duration="1.4s">
                <div>
                  <div className="text-center">
                    <Clock3 className="mx-auto" size={30} style={{ color: theme.primaryColor }} />

                    <h2 className="mt-3 text-xl font-black sm:text-2xl">{t("public.times_title")}</h2>

                    <p className="mt-1 text-sm opacity-65">
                      {t("public.times_description", {
                        date: new Date(`${date}T00:00:00`).toLocaleDateString("pt-BR"),
                      })}
                    </p>
                  </div>

                  {slots.some((slot) => slot.isAvailable) ? (
                    <div className="mx-auto mt-6 grid max-w-2xl grid-cols-3 gap-2 sm:grid-cols-5">
                      {slots.map((slot, index) => (
                        <Animate
                          key={slot.time}
                          type={AnimationTypes.fadeInTraslate}
                          duration="1.2s"
                          delay={`${Math.min(index, 8) * 0.06}s`}
                        >
                          <button
                            type="button"
                            disabled={!slot.isAvailable}
                            data-interaction-description-key="interactionDescriptions:restaurantReservations.public.time"
                            onClick={() => setTime(slot.time)}
                            className="rounded-xl border px-3 py-3 text-sm font-black transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-30"
                            style={
                              time === slot.time
                                ? {
                                    backgroundColor: theme.serviceSelectorActiveBackgroundColor,
                                    borderColor: theme.serviceSelectorActiveBackgroundColor,
                                    color: theme.serviceSelectorActiveTextColor,
                                  }
                                : {
                                    backgroundColor: theme.surfaceColor,
                                    borderColor: theme.borderColor,
                                    color: theme.textColor,
                                  }
                            }
                          >
                            {slot.time.slice(0, 5)}
                          </button>
                        </Animate>
                      ))}
                    </div>
                  ) : (
                    <div
                      className="mx-auto mt-6 max-w-xl rounded-2xl p-5 text-center text-sm font-semibold"
                      style={mutedSurfaceStyle}
                    >
                      {t("public.no_times")}
                    </div>
                  )}
                  <div className="mt-7 flex gap-3">
                    <button
                      type="button"
                      onClick={() => setStep("details")}
                      data-interaction-description-key="interactionDescriptions:restaurantReservations.public.backToDetails"
                      className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl border px-4 font-black transition active:scale-[0.98]"
                      style={{ borderColor: theme.borderColor, color: theme.textColor }}
                    >
                      <ArrowLeft size={18} /> <span className="hidden sm:inline">{t("actions.back")}</span>
                    </button>

                    <button
                      type="button"
                      disabled={!time}
                      onClick={() => setStep("confirm")}
                      data-interaction-description-key="interactionDescriptions:restaurantReservations.public.continueToConfirm"
                      className="inline-flex min-h-12 flex-1 items-center justify-center gap-2 rounded-2xl px-5 text-sm font-black shadow-lg transition hover:-translate-y-0.5 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
                      style={primaryButtonStyle}
                    >
                      {t("actions.continue")} <ArrowRight size={18} />
                    </button>
                  </div>
                </div>
              </Animate>
            )}

            {step === "confirm" && (
              <Animate type={AnimationTypes.slideRightIn} duration="1.5s">
                <div>
                  <div className="text-center">
                    <Check className="mx-auto" size={30} style={{ color: theme.primaryColor }} />

                    <h2 className="mt-3 text-xl font-black sm:text-2xl">{t("public.confirm_title")}</h2>

                    <p className="mt-1 text-sm opacity-65">{t("public.confirm_description")}</p>
                  </div>

                  <div
                    className="mx-auto mt-6 grid max-w-2xl gap-3 rounded-2xl p-4 text-sm font-bold sm:grid-cols-2"
                    style={mutedSurfaceStyle}
                  >
                    <span className="flex items-center gap-2">
                      <MapPin size={17} style={{ color: theme.primaryColor }} />
                      {t("public.summary_area", { area: selectedArea?.name })}
                    </span>

                    <span className="flex items-center gap-2">
                      <Users size={17} style={{ color: theme.primaryColor }} />
                      {t("public.summary_people", { count: numericPartySize })}
                    </span>

                    <span className="flex items-center gap-2">
                      <CalendarDays size={17} style={{ color: theme.primaryColor }} />
                      {t("public.summary_date", { date: new Date(`${date}T00:00:00`).toLocaleDateString("pt-BR") })}
                    </span>

                    <span className="flex items-center gap-2">
                      <Clock3 size={17} style={{ color: theme.primaryColor }} />
                      {t("public.summary_time", { time: time.slice(0, 5) })}
                    </span>
                  </div>

                  <div className="mx-auto mt-4 max-w-2xl">
                    <InputField
                      type="text"
                      value={notes}
                      maxLength={300}
                      placeholder={t("fields.notes")}
                      descriptionKey="interactionDescriptions:restaurantReservations.public.notes"
                      onChange={(event) => setNotes(event.target.value)}
                    />
                  </div>

                  {!auth.customer && (
                    <p
                      className="mx-auto mt-4 max-w-2xl rounded-xl px-4 py-3 text-center text-xs font-semibold"
                      style={mutedSurfaceStyle}
                    >
                      {t("public.login_hint")}
                    </p>
                  )}

                  <div className="mt-7 flex gap-3">
                    <button
                      type="button"
                      onClick={() => setStep("time")}
                      data-interaction-description-key="interactionDescriptions:restaurantReservations.public.backToTime"
                      className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl border px-4 font-black transition active:scale-[0.98]"
                      style={{ borderColor: theme.borderColor, color: theme.textColor }}
                    >
                      <ArrowLeft size={18} /> <span className="hidden sm:inline">{t("actions.back")}</span>
                    </button>

                    <button
                      type="button"
                      data-interaction-description-key="interactionDescriptions:restaurantReservations.public.confirm"
                      disabled={reservationsApi.isLoading}
                      onClick={createReservation}
                      className="inline-flex min-h-12 flex-1 items-center justify-center gap-2 rounded-2xl px-5 text-sm font-black shadow-lg transition hover:-translate-y-0.5 active:scale-[0.98] disabled:opacity-50"
                      style={primaryButtonStyle}
                    >
                      {reservationsApi.isLoading
                        ? t("common.loading")
                        : auth.customer
                          ? t("public.confirm")
                          : t("public.login_to_confirm")}
                      <Check size={18} />
                    </button>
                  </div>
                </div>
              </Animate>
            )}

            {step === "success" && (
              <Animate type={AnimationTypes.zoomIn} duration="1.6s">
                <div className="py-6 text-center">
                  <div
                    className="mx-auto flex h-16 w-16 items-center justify-center rounded-full"
                    style={{ backgroundColor: theme.statusCardBackgroundColor, color: theme.buttonColor }}
                  >
                    <Check size={32} strokeWidth={3} />
                  </div>

                  <h2 className="mt-5 text-2xl font-black">{t("public.success_title")}</h2>

                  <p className="mx-auto mt-2 max-w-md text-sm opacity-65">{t("public.success")}</p>

                  <button
                    type="button"
                    onClick={startAnotherReservation}
                    data-interaction-description-key="interactionDescriptions:restaurantReservations.public.newReservation"
                    className="mt-6 inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl px-6 text-sm font-black shadow-lg transition hover:-translate-y-0.5 active:scale-[0.98]"
                    style={primaryButtonStyle}
                  >
                    {t("public.new_reservation")} <ArrowRight size={18} />
                  </button>
                </div>
              </Animate>
            )}
          </div>
        </section>

        {auth.customer && (
          <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
            <section className="rounded-lg p-5 shadow-sm" style={cardStyle}>
              <h2 className="text-lg font-black">{t("public.mine_title")}</h2>
              {mine.length === 0 ? (
                <p className="mt-3 text-sm opacity-60">{t("public.mine_empty")}</p>
              ) : (
                <div className="mt-4 space-y-3">
                  {mine.map((reservation, index) => (
                    <Animate
                      key={reservation.id}
                      type={AnimationTypes.fadeInTraslate}
                      duration="1.3s"
                      delay={`${Math.min(index, 6) * 0.08}s`}
                    >
                      <article className="rounded-lg border p-4" style={{ borderColor: theme.borderColor }}>
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="font-black">{reservation.areaName}</p>

                              <span
                                className="rounded-full px-2.5 py-1 text-xs font-black"
                                style={
                                  reservation.status === "Confirmed"
                                    ? { backgroundColor: theme.statusCardBackgroundColor, color: theme.buttonColor }
                                    : mutedSurfaceStyle
                                }
                              >
                                {t(`status.${reservation.status}`)}
                              </span>
                            </div>

                            <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2 text-sm font-semibold opacity-70">
                              <span className="flex items-center gap-1.5">
                                <CalendarDays size={16} />
                                {new Date(reservation.startsAt).toLocaleDateString("pt-BR")}
                              </span>

                              <span className="flex items-center gap-1.5">
                                <Clock3 size={16} />
                                {new Date(reservation.startsAt).toLocaleTimeString("pt-BR", {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </span>

                              <span className="flex items-center gap-1.5">
                                <Users size={16} />
                                {reservation.partySize} {t("common.people")}
                              </span>
                            </div>
                          </div>

                          {reservation.status === "Confirmed" &&
                            new Date(reservation.startsAt).getTime() > renderedAt && (
                              <button
                                type="button"
                                disabled={reservationsApi.isLoading}
                                data-interaction-description-key="interactionDescriptions:restaurantReservations.public.cancel"
                                onClick={() =>
                                  setNewConfirmation(t("actions.cancel"), t("public.cancel_confirmation"), async () => {
                                    if (await reservationsApi.cancelMine(restaurantSlug, reservation.id))
                                      await loadMine()
                                  })
                                }
                                className="shrink-0 rounded-lg border border-red-200 px-4 py-2.5 text-sm font-black text-red-600 transition hover:bg-red-50 disabled:opacity-50"
                              >
                                {t("actions.cancel")}
                              </button>
                            )}
                        </div>

                        {reservation.notes && (
                          <p
                            className="mt-3 border-t pt-3 text-sm font-medium opacity-65"
                            style={{ borderColor: theme.borderColor }}
                          >
                            {reservation.notes}
                          </p>
                        )}
                      </article>
                    </Animate>
                  ))}
                </div>
              )}
            </section>
          </Animate>
        )}
      </div>
    </Animate>
  )
}
